import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { supabase } from '../../lib/supabaseClient';
import toast from 'react-hot-toast';
import { useNavigate, Link } from 'react-router-dom';
import { mapSupabaseErrorMessage } from './authErrors';
import AnimatedBackground from '../../components/AnimatedBackground';
import Logo from '../../components/Logo';

const forgotPasswordSchema = z.object({
  email: z.email('Please enter a valid email address'),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    const trimmedEmail = data.email.trim();

    // Check if the email exists by attempting to sign in with a dummy password
    // This helps us determine if the user exists before sending a reset email
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: trimmedEmail,
      password: 'dummy_check_password_12345!@#$%^&*()',
    });

    // Check if the error specifically indicates the user doesn't exist
    if (signInError) {
      const errorMessage = signInError.message.toLowerCase();

      // If error specifically says user_not_found, user doesn't exist
      if (errorMessage.includes('user_not_found')) {
        toast.error('No account found for this email address. Please sign up first.');
        return;
      }

      // For "invalid login credentials", Supabase doesn't differentiate between
      // wrong password and non-existent user for security reasons.
      // We'll proceed to resetPasswordForEmail and check its error response.
    }

    // Proceed with password reset
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(trimmedEmail, {
      redirectTo: undefined, // No redirect, we'll use OTP code
    });

    if (resetError) {
      const errorMsg = resetError.message.toLowerCase();
      // Check if the error indicates user doesn't exist
      if (errorMsg.includes('user_not_found') ||
          errorMsg.includes('email not found') ||
          errorMsg.includes('user does not exist')) {
        toast.error('No account found for this email address. Please sign up first.');
      } else {
        // For other errors, show toast
        toast.error(mapSupabaseErrorMessage(resetError.message));
      }
      return;
    }

    // Success - email sent (only if user exists)
    toast.success('Check your email for codes');
    navigate('/reset-password/verify', { state: { email: trimmedEmail } });
  };

  return (
    <div className="eg-auth-page auth-page">
      <AnimatedBackground />
      <div className="relative z-10 w-full max-w-2xl flex flex-col items-center">
        <div className="mb-4 pb-8 w-full">
          <Logo />
        </div>
        <div className="eg-auth-card">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-light tracking-wide" style={{ color: 'var(--color-auth-text)' }}>Forgot Password</h2>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <input
                type="email"
                placeholder="Email"
                {...register('email')}
                className={`eg-auth-input ${errors.email ? 'input-error' : ''}`}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-400">{errors.email.message}</p>
              )}
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="eg-auth-btn"
            >
              {isSubmitting ? 'Sending…' : 'Send reset link'}
            </button>
          </form>
          <div className="mt-3 text-sm text-center" style={{ color: 'var(--color-auth-text-secondary)' }}>
            <span>
              Remember password?{' '}
              <Link to="/login" className="inline-block py-1 underline hover:text-[#17c480] transition-colors focus:outline-none focus:ring-2 focus:ring-[#17c480] rounded" style={{ color: 'var(--color-auth-link)' }}>
                Login
              </Link>
            </span>
          </div>
          <div className="mt-4 text-xs text-center" style={{ color: 'var(--color-auth-text-secondary)' }}>
            <p>If you have an Engramiq account you will receive an email with password reset instructions.</p>
          </div>
          <div className="mt-4 text-xs text-center" style={{ color: 'var(--color-auth-text-muted)' }}>
            <p>Need help? Contact <a href="mailto:support@engramiq.com" className="underline hover:text-[#17c480] transition-colors">support@engramiq.com</a></p>
          </div>
        </div>
      </div>
      {/* <SupportChat /> */}
    </div>
  );
};

export default ForgotPasswordPage;
